import React, { useMemo, useState } from "react";
import { X, PawPrint, Footprints, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const PACE_OPTIONS = ["Casual", "Active", "Endurance"];
const TRANSIT_OPTIONS = [
  { v: "Transit", label: "Public transport" },
  { v: "Car", label: "Car" },
];
const ACTIVITY_OPTIONS = ["Senior", "Moderate", "High"];

/**
 * Full-screen onboarding modal — "Personalize Your Pack".
 *
 * Renders ONLY when:
 *   1. The walker is signed in.
 *   2. Either `humanProfile` OR `dogProfile` is null (i.e. genuinely missing —
 *      not just "user hasn't filled every field").
 *   3. They haven't already skipped it this session (`sessionStorage`).
 *
 * Only the missing half is asked for — a walker who has a dog profile but no
 * pace preference sees just the Pace card. On submit we PATCH each half
 * separately so the existing partial-update semantics are preserved.
 */
export default function PersonalizePackModal() {
  const { user, refresh } = useAuth();

  const needsHuman = !!user && user.humanProfile == null;
  const needsDog = !!user && user.dogProfile == null;
  const shouldShow = needsHuman || needsDog;

  const skipKey = "td_personalize_skipped";
  const [dismissedThisSession, setDismissedThisSession] = useState(() => {
    try { return sessionStorage.getItem(skipKey) === "1"; } catch { return false; }
  });

  const [pace, setPace] = useState("");
  const [transitReliance, setTransitReliance] = useState("");
  const [dogName, setDogName] = useState("");
  const [dogBreed, setDogBreed] = useState("");
  const [dogAge, setDogAge] = useState("");
  const [dogActivity, setDogActivity] = useState("");
  const [saving, setSaving] = useState(false);

  const humanReady = !needsHuman || (pace && transitReliance);
  const dogReady = !needsDog || (dogActivity && dogName.trim());
  const canSubmit = humanReady && dogReady && !saving;

  const dismiss = () => {
    try { sessionStorage.setItem(skipKey, "1"); } catch { /* ignore */ }
    setDismissedThisSession(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // PATCH each half separately — matches the semantics the existing
      // Profile page uses so admins see consistent audit rows regardless of
      // which surface the walker filled from.
      if (needsHuman) {
        await api.patch("/auth/me/matchmaker-profile", {
          humanProfile: { pace, transitReliance },
        });
      }
      if (needsDog) {
        await api.patch("/auth/me/matchmaker-profile", {
          dogProfile: {
            name: dogName.trim(),
            breed: dogBreed.trim(),
            age: dogAge === "" ? null : parseFloat(dogAge),
            activityLevel: dogActivity,
          },
        });
      }
      toast.success("Pack saved — your Discover feed just got smarter.");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save right now.");
    } finally {
      setSaving(false);
    }
  };

  const humanCard = useMemo(() => (
    <div className="td-card p-5 sm:p-6" data-testid="onboarding-human-card">
      <div className="flex items-center gap-2">
        <Footprints size={18} className="text-brand-primary" />
        <h3 className="font-heading text-lg">My Pace</h3>
      </div>
      <p className="text-sm text-brand-subtle mt-1">
        How you walk, and how you get to the trailhead.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="td-section-label">Pace</span>
          <select
            data-testid="onboarding-pace"
            className="td-input mt-1"
            value={pace}
            onChange={(e) => setPace(e.target.value)}
          >
            <option value="">Choose…</option>
            {PACE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="td-section-label">Transit reliance</span>
          <select
            data-testid="onboarding-transit"
            className="td-input mt-1"
            value={transitReliance}
            onChange={(e) => setTransitReliance(e.target.value)}
          >
            <option value="">Choose…</option>
            {TRANSIT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </label>
      </div>
    </div>
  ), [pace, transitReliance]);

  const dogCard = useMemo(() => (
    <div className="td-card p-5 sm:p-6" data-testid="onboarding-dog-card">
      <div className="flex items-center gap-2">
        <PawPrint size={18} className="text-brand-accent" />
        <h3 className="font-heading text-lg">My Pack</h3>
      </div>
      <p className="text-sm text-brand-subtle mt-1">
        Tell us about your dog — we&apos;ll weight suggestions by breed, age and energy.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block sm:col-span-2">
          <span className="td-section-label">Dog&apos;s name</span>
          <input
            data-testid="onboarding-dog-name"
            className="td-input mt-1"
            value={dogName}
            onChange={(e) => setDogName(e.target.value)}
            placeholder="e.g. Ozzy"
            maxLength={60}
          />
        </label>
        <label className="block">
          <span className="td-section-label">Breed</span>
          <input
            data-testid="onboarding-dog-breed"
            className="td-input mt-1"
            value={dogBreed}
            onChange={(e) => setDogBreed(e.target.value)}
            placeholder="e.g. Border Collie"
            maxLength={80}
          />
        </label>
        <label className="block">
          <span className="td-section-label">Age (years)</span>
          <input
            data-testid="onboarding-dog-age"
            type="number"
            step="0.5"
            min="0"
            max="30"
            className="td-input mt-1"
            value={dogAge}
            onChange={(e) => setDogAge(e.target.value)}
            placeholder="e.g. 4"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="td-section-label">Activity level</span>
          <select
            data-testid="onboarding-dog-activity"
            className="td-input mt-1"
            value={dogActivity}
            onChange={(e) => setDogActivity(e.target.value)}
          >
            <option value="">Choose…</option>
            {ACTIVITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
      </div>
    </div>
  ), [dogName, dogBreed, dogAge, dogActivity]);

  if (!shouldShow || dismissedThisSession) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] bg-brand-text/70 flex items-start sm:items-center justify-center overflow-y-auto"
      data-testid="onboarding-modal"
    >
      <div className="min-h-full sm:min-h-0 w-full sm:max-w-3xl bg-brand-surface sm:rounded-3xl relative animate-fade-up">
        <button
          data-testid="onboarding-skip"
          onClick={dismiss}
          aria-label="Skip for now"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-brand-sand text-brand-text flex items-center justify-center hover:bg-brand-border/60"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="td-section-label flex items-center gap-1.5 text-brand-accent">
            <Sparkles size={12} /> Personalize your pack
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl text-brand-text mt-2 leading-tight">
            Let&apos;s tailor Discover to <em className="text-brand-primary">your</em> walks.
          </h2>
          <p className="text-sm text-brand-subtle mt-2 max-w-lg">
            Two quick questions each. TrailDog uses this to weight the &ldquo;best walk near you&rdquo; score so the trails you see actually fit your pack. Change any of it anytime from Profile.
          </p>

          <div className="mt-6 space-y-4">
            {needsHuman && humanCard}
            {needsDog && dogCard}
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              data-testid="onboarding-skip-secondary"
              onClick={dismiss}
              className="td-btn-secondary !py-2.5 order-2 sm:order-1"
            >
              Skip for now
            </button>
            <button
              data-testid="onboarding-save"
              onClick={save}
              disabled={!canSubmit}
              className="td-btn-primary !py-2.5 order-1 sm:order-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Save & personalise
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
